/**
 * Inventory Audit Data Table page.
 *
 * Wraps the page in AuditProvider (scoped context for this feature).
 * Loads available runs on mount, defaults to the latest.
 * Delegates rendering to InventoryAuditPageContent which consumes the context.
 */
import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

import Layout from "../../components/Layout"
import AuditFreshnessBar from "../../components/audit/AuditFreshnessBar"
import AuditAlertsButton from "../../components/audit/AuditAlertsButton"
import AuditHelpButton from "../../components/audit/AuditHelpButton"
import AuditStoreTabs, { ALL_ROOFTOPS, readRememberedStore, rememberStore } from "../../components/audit/AuditStoreTabs"
import AuditTabs, { WEBSITE_TAB_SLUGS, WebsiteTabSlug } from "../../components/audit/AuditTabs"
import DownloadMenu from "../../components/audit/DownloadMenu"
import WorklistDownloadButton from "../../components/audit/WorklistDownloadButton"
import WebsiteSheetSection from "../../components/audit/WebsiteSheetSection"
import { AuditProvider, useAudit } from "../../context/AuditContext"
import { useAuth } from "../../context/AuthContext"
import { useDealerGroup } from "../../context/DealerGroupContext"
import {
  AuditFileStatus,
  AuditRooftopOption,
  AuditRunSummary,
  fetchAuditFileStatus,
  getChainedWebsiteRun,
  listAuditRooftops,
  listAuditRuns,
} from "../../api/audit"
import AuditTable from "../../components/audit/AuditTable"
import AuditWorklistView from "../../components/audit/worklist/AuditWorklistView"
import ErrorBoundary from "../../components/ErrorBoundary"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import FilterBar from "../../components/audit/FilterBar"
import LegendStrip from "../../components/audit/LegendStrip"
import FilterChips from "../../components/audit/FilterChips"
import PaginationFooter from "../../components/audit/PaginationFooter"
import { THEME } from "../../config/theme"

// Last-chosen store view, so a desk manager's selection survives reload —
// same persistence shape as the dealer-group switcher's key.

export default function InventoryAuditPage() {
  return (
    <Layout>
      <AuditProvider>
        <InventoryAuditPageContent />
      </AuditProvider>
    </Layout>
  )
}

function InventoryAuditPageContent() {
  // Working surface: "All Groups" (dealerGroupId === null) isn't a valid context
  // — operator must pick one group. Runs reload when the active group changes.
  const { dealerGroupId } = useDealerGroup()
  const { user, hasPermission } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    currentRunId,
    setCurrentRunId,
    currentSheet,
    loadColumnsForSheet,
    setPage,
    loadVehicles,
  } = useAudit()

  const [runs, setRuns] = useState<AuditRunSummary[]>([])
  const [runsLoading, setRunsLoading] = useState<boolean>(true)
  const [runsError, setRunsError] = useState<string | null>(null)

  // The page-level store view: which store's problems the page shows. A
  // convenience default, not a security boundary (Ian, 2026-09-11) — anyone
  // can switch stores or view all. Seeded URL → this visit's last choice →
  // the user's home store → all, mirroring DealerGroupContext's seed order.
  const [rooftopOptions, setRooftopOptions] = useState<AuditRooftopOption[]>([])
  const [rooftopSlug, setRooftopSlug] = useState<string>(
    () =>
      searchParams.get("rooftop") ??
      readRememberedStore(user?.id) ??
      user?.rooftop?.slug ??
      ALL_ROOFTOPS
  )

  // Load the group's stores for the dropdown, and revalidate the selection
  // against them — a stale slug (another group's URL, a renamed store) falls
  // back to the user's home store or All stores instead of 404ing every
  // fetch on the page.
  useEffect(() => {
    if (dealerGroupId === null) {
      setRooftopOptions([])
      return
    }
    let cancelled = false
    listAuditRooftops(dealerGroupId)
      .then((response) => {
        if (cancelled) return
        setRooftopOptions(response.rooftops)
        setRooftopSlug((current) => {
          if (current === ALL_ROOFTOPS) return current
          if (response.rooftops.some((r) => r.slug === current)) return current
          const home = user?.rooftop?.slug
          return home && response.rooftops.some((r) => r.slug === home)
            ? home
            : ALL_ROOFTOPS
        })
      })
      .catch(() => {
        // No options, no dropdown — the page still works as All stores.
        if (!cancelled) setRooftopOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, user])

  // Persist the choice and mirror it into the URL so the view is shareable —
  // replace, not push, so switching stores doesn't stack history entries.
  useEffect(() => {
    rememberStore(user?.id, rooftopSlug)
    setSearchParams(
      (params) => {
        if (rooftopSlug === ALL_ROOFTOPS) params.delete("rooftop")
        else params.set("rooftop", rooftopSlug)
        return params
      },
      { replace: true }
    )
  }, [rooftopSlug, setSearchParams, user?.id])

  // Applied only once the loaded options confirm the slug — until then the
  // page reads as All stores rather than erroring on a slug the backend
  // would refuse.
  const activeRooftopSlug =
    rooftopSlug !== ALL_ROOFTOPS && rooftopOptions.some((r) => r.slug === rooftopSlug)
      ? rooftopSlug
      : null
  const activeRooftop =
    rooftopOptions.find((r) => r.slug === activeRooftopSlug) ?? null

  // Staged-file status feeds only the freshness bar's "files from" clause —
  // the upload machinery itself lives on the New Audit Run page.
  const [auditStatus, setAuditStatus] = useState<AuditFileStatus | null>(null)

  useEffect(() => {
    if (dealerGroupId === null) {
      setAuditStatus(null)
      return
    }
    let cancelled = false
    fetchAuditFileStatus(dealerGroupId)
      .then((status) => {
        if (!cancelled) setAuditStatus(status)
      })
      .catch(() => {
        // A transient fetch failure shouldn't block the page; the bar simply
        // omits the "files from" clause.
        if (!cancelled) setAuditStatus(null)
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId])

  // Load this group's runs, re-fetching whenever the active group changes.
  useEffect(() => {
    if (dealerGroupId === null) {
      setRuns([])
      setCurrentRunId(null)
      setRunsLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setRunsLoading(true)
      setRunsError(null)
      try {
        const response = await listAuditRuns(30, dealerGroupId)
        if (cancelled) return
        setRuns(response.runs)
        // Keep a valid selection; if the current run isn't in this group's runs
        // (initial load, or a group switch), jump to the group's latest.
        const stillValid = response.runs.some((r) => r.id === currentRunId)
        if (!stillValid) setCurrentRunId(response.runs[0]?.id ?? null)
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : "Failed to load runs"
        setRunsError(message)
      } finally {
        if (!cancelled) setRunsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load column config whenever the sheet changes
  useEffect(() => {
    loadColumnsForSheet(currentSheet)
  }, [currentSheet, loadColumnsForSheet])

  // Reset to page 1 when sheet changes (user expects fresh view)
  useEffect(() => {
    setPage(1)
  }, [currentSheet, setPage])

  // Load vehicles whenever run / sheet / page / size changes
  useEffect(() => {
    loadVehicles()
  }, [loadVehicles])

  const currentRun = runs.find((r) => r.id === currentRunId) ?? null

  // Worklist is the remodel's default reading of a run; Sheet view preserves
  // the original five-tab table untouched.
  const [view, setView] = useState<AuditViewMode>("worklist")

  // The run's auto-chained website run feeds the folded-in website tabs and
  // the website workbook download (the standalone page is retired). Null for
  // manual-era runs and failed chains — the tabs simply don't render.
  const [websiteRun, setWebsiteRun] = useState<AuditRunSummary | null>(null)
  const [websiteSheet, setWebsiteSheet] = useState<WebsiteTabSlug | null>(null)

  // Deep link from the retired-page redirect (old /inventory/website
  // bookmarks): ?wsheet=matched opens the folded website tab once the chained
  // run has loaded — but only while the sheet view exists to open it in.
  const pendingWebsiteSheet = useRef<WebsiteTabSlug | null>(
    (WEBSITE_TAB_SLUGS as readonly string[]).includes(searchParams.get("wsheet") ?? "")
      ? (searchParams.get("wsheet") as WebsiteTabSlug)
      : null
  )

  const refreshChainedRun = React.useCallback(() => {
    if (currentRunId === null) {
      setWebsiteRun(null)
      return
    }
    getChainedWebsiteRun(currentRunId)
      .then((response) => {
        setWebsiteRun(response.run)
        // Gated on SHEET_VIEW_ENABLED: with the sheet view parked there is no
        // toggle back, so honoring the link stranded the operator on a view
        // they could not leave. The link now lands on the worklist.
        if (SHEET_VIEW_ENABLED && response.run && pendingWebsiteSheet.current !== null) {
          setView("sheet")
          setWebsiteSheet(pendingWebsiteSheet.current)
          pendingWebsiteSheet.current = null
        }
      })
      .catch(() => setWebsiteRun(null))
  }, [currentRunId])
  useEffect(() => {
    setWebsiteSheet(null)
    refreshChainedRun()
  }, [refreshChainedRun])

  return (
    <div style={{ padding: THEME.spacing.lg }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: THEME.spacing.sm,
          marginBottom: THEME.spacing.md,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 500,
              letterSpacing: "-0.5px",
              margin: 0,
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            Inventory Audit
          </h1>
        </div>
        {dealerGroupId !== null && (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: THEME.spacing.md }}>
            {SHEET_VIEW_ENABLED && <ViewToggle view={view} onChange={setView} />}
            {/* The run selector lives in the freshness bar below — having it
                here too repeated the same run date twice on one screen
                (Ian, 2026-09-14). One button, one workbook — a sheet per
                flag. The old two-option menu belongs to the sheet view (its
                "Current View" export needs a current view), so it goes with
                that branch. Help opens the pop-up that replaced the worklist's
                always-on legend strip (Ian, 2026-09-28). */}
            {/* Alerts: who gets emailed about which problems every morning
                (revenue-assurance plan, Phase 2). Deep-links to the dealer's
                Scheduled Reports page, where the alerts live. The page owns
                the route string, as with Start new run. */}
            {hasPermission("manage_audit_alerts") && (
              <AuditAlertsButton onOpen={() => navigate("/reports/scheduled")} />
            )}
            <AuditHelpButton />
            {SHEET_VIEW_ENABLED
              ? <DownloadMenu currentRun={currentRun} />
              : <WorklistDownloadButton currentRun={currentRun} rooftop={activeRooftop} />}
          </div>
        )}
      </div>

      {/* The store view reads as the page's identity, not a filter control —
          a tab strip under the title (Ian's pick, 2026-09-11), same pattern
          as the Daily Photo List's sheet tabs. */}
      {dealerGroupId !== null && rooftopOptions.length > 0 && (
        <AuditStoreTabs
          options={rooftopOptions}
          value={rooftopSlug}
          onChange={setRooftopSlug}
        />
      )}

      {dealerGroupId !== null && (
        <AuditFreshnessBar
          runs={runs}
          status={auditStatus}
          loading={runsLoading}
          runsError={runsError}
          onStartNewRun={() => navigate("/inventory/audit/new-run")}
        />
      )}

      {dealerGroupId === null ? (
        <DealerGroupRequiredNotice surface="inventory audits" />
      ) : view === "worklist" ? (
        // Nothing until the run list lands. This used to read
        // `view === "worklist" && currentRunId !== null`, which sent the FIRST
        // paint down the sheet-view branch and then swapped — the flash Ian
        // reported on every refresh.
        currentRunId === null ? null : (
          // The worklist is the page. A fault inside it should cost the
          // worklist and leave the run selector, the download button and the
          // freshness bar above it usable — before this, it took the whole
          // page to a white screen.
          <ErrorBoundary surface="inventory-worklist">
            <AuditWorklistView runId={currentRunId} rooftopSlug={activeRooftopSlug} />
          </ErrorBoundary>
        )
      ) : (
        <>
          <AuditTabs
            run={currentRun}
            websiteRun={websiteRun}
            activeWebsiteSheet={websiteSheet}
            onWebsiteSheetSelect={setWebsiteSheet}
            onSheetChange={() => setWebsiteSheet(null)}
          />

          {websiteSheet !== null && websiteRun ? (
            <WebsiteSheetSection
              run={websiteRun}
              sheet={websiteSheet}
              dealerGroupId={dealerGroupId}
              // Latest run only: a manual re-check always compares against
              // the newest workbook.
              showRecheck={currentRunId === (runs[0]?.id ?? null)}
              onRecheckComplete={refreshChainedRun}
            />
          ) : (
            <>
              <LegendStrip />

              <FilterBar />
              <FilterChips />

              <AuditTable />
              <PaginationFooter />
            </>
          )}
        </>
      )}
    </div>
  )
}

type AuditViewMode = "worklist" | "sheet"

/**
 * Sheet view is switched off (Ian, 2026-08-12: "i want to disable sheet view…
 * it looks ugly… its not useable anyways"). The toggle is hidden and the page
 * always renders the worklist.
 *
 * The branch itself is left standing rather than deleted: the folded-in website
 * tabs (`AuditTabs`, `WebsiteSheetSection`) hang off it, so removing it is its
 * own scoped piece of work, not a side effect of hiding a button. Flip this to
 * bring the toggle back.
 */
const SHEET_VIEW_ENABLED = false

/** Worklist / Sheet view switch — mockup `.toggle`: bordered pair, navy active. */
function ViewToggle({
  view,
  onChange,
}: {
  view: AuditViewMode
  onChange: (next: AuditViewMode) => void
}) {
  const optionStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 12px",
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: THEME.fonts.primary,
    border: "none",
    cursor: "pointer",
    background: active ? THEME.colors.navy : THEME.colors.white,
    color: active ? THEME.colors.white : THEME.colors.neutral.light.textSecondary,
  })
  return (
    <span
      style={{
        display: "inline-flex",
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.sm,
        overflow: "hidden",
      }}
    >
      <button type="button" style={optionStyle(view === "worklist")} onClick={() => onChange("worklist")}>
        Worklist
      </button>
      <button type="button" style={optionStyle(view === "sheet")} onClick={() => onChange("sheet")}>
        Sheet view
      </button>
    </span>
  )
}
